/**
 * Min side: the employee's operational home for today (owner's employee UI brief, 2026-09-22).
 * The work state first, then today's shift, its tasks and who works alongside, the next shifts,
 * open shifts only when there are any, a swap waiting for an answer, and the vacation balance as
 * a shortcut. The month grid, the absence form, availability, the profile and the documents live
 * on their own pages (Min plan, Fravær, Profil). Every call is a route the phone already uses.
 */
import {
  type ApiShift,
  useMe,
  useMyShifts,
  useMySwaps,
  useOpenShifts,
  usePunchStatus,
  useShifts,
} from '@/api/data';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { today } from '@/rules';
import { breakForEmployee } from '@/rules/breakSetting';
import { useTenant } from '@/shell/tenant';
import { Chip, LoadError } from '@/ui';
import { DNL, fmtD, fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { addDays, dow } from '@timefly/core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Card,
  OpenDrawer,
  OpenRow,
  Progress,
  ShiftDrawer,
  ShiftRow,
  SwapList,
  WorkCard,
  dayOf,
  shiftName,
  span,
  taskCount,
  useAsk,
  useNow,
} from './min/parts';

export default function Min() {
  const user = useAuth((s) => s.user);
  const { tenant, workspace } = useTenant();
  const day0 = today();
  const now = useNow();
  const statusQ = usePunchStatus();
  const shiftsQ = useMyShifts(day0, addDays(day0, 13));
  // nothing module-bound is asked for before the company has answered
  const scheduling = !!tenant && workspace.enabled.scheduling;
  const openQ = useOpenShifts(day0, addDays(day0, 14), scheduling);
  const seeColleagues = scheduling && workspace.visibility.coworker_shifts;
  const teamQ = useShifts(day0, day0, seeColleagues);
  const swapsQ = useMySwaps();
  const meQ = useMe(user?.id);
  const a = useAsk();
  const [detail, setDetail] = useState<ApiShift | null>(null);
  const [openDetail, setOpenDetail] = useState<ApiShift | null>(null);

  const st = statusQ.data;
  const mine = (shiftsQ.data?.shifts ?? []).sort((x, y) => x.start_time.localeCompare(y.start_time));
  const todays = mine.filter((s) => dayOf(s) === day0);
  const later = mine.filter((s) => dayOf(s) > day0);
  // the shift the day is about: the open one, else the first of today not yet finished, else today's first
  const main =
    (st?.shift && todays.find((s) => s.id === st.shift?.id)) ||
    todays.find((s) => !s.punch_out_time) ||
    todays[0] ||
    null;
  const tasks = taskCount(main);
  const colleagues = seeColleagues
    ? (teamQ.data?.shifts ?? []).filter((s) => s.published && s.user_id != null && s.user_id !== user?.id)
    : [];
  const nowIso = now.toISOString();
  const onNow = colleagues.filter((s) => s.start_time <= nowIso && s.end_time > nowIso);
  const alongside = main
    ? colleagues.filter((s) => s.start_time < main.end_time && s.end_time > main.start_time)
    : [];
  const open = scheduling ? (openQ.data ?? []) : [];
  const waiting = (swapsQ.data ?? []).filter(
    (x) => x.offered_by_two === user?.id && x.status === 'pending_user_approval',
  );
  const me = meQ.data;
  // the list's copy is fresher than the one captured on click (a task ticked in the drawer)
  const shown = detail ? (mine.find((s) => s.id === detail.id) ?? detail) : null;

  return (
    <>
      <div className="ph">
        <div>
          <h1>{T('Min side')}</h1>
          <div className="sub">
            {user ? fullName(user) : ''}, {tenant?.name ?? ''}, {DNL[dow(day0)]} {fmtD(day0)}
          </div>
        </div>
      </div>

      <div className="egrid">
        <section className="card2 ecard ewrap" aria-label={T('Arbeidsstatus')}>
          <WorkCard
            st={st}
            loading={statusQ.isPending}
            todays={todays}
            nextShift={later[0] ?? st?.nextShift ?? null}
            onDetails={setDetail}
            shifts={shiftsQ.status}
          />
        </section>
        {/* one card for today under the clock card: the shift once, with its state, the colleagues
            alongside and the break; «Dagens vakt» was the same shift a third time (#272 B-36) */}
        <Card
          title={T('I dag')}
          sub={`${DNL[dow(day0)]} ${fmtD(day0)}`}
          actions={
            !main ? null : main.punch_out_time ? (
              <Chip tone="neu" small>
                {T('Ferdig')}
              </Chip>
            ) : main.punch_in_time ? (
              <Chip tone="ok" small>
                {T('På jobb')}
              </Chip>
            ) : main.start_time > nowIso ? (
              <Chip tone="warn" small>
                {T('Starter senere')}
              </Chip>
            ) : null
          }
          testid="today"
        >
          {main ? (
            <ShiftRow s={main} place={tenant?.name} onOpen={() => setDetail(main)} showDate={false} />
          ) : (
            <div className="ekv">
              <Icon name="calendar" />
              <span>{T('Vakt')}</span>
              {/* unknown is not «Ingen vakt»: the error itself shows under Neste vakter (#272 B-30) */}
              <b>{shiftsQ.isSuccess ? T('Ingen vakt') : '–'}</b>
            </div>
          )}
          {tasks.total > 0 && (
            <div className="ekv">
              <Icon name="check" />
              <span>{T('Oppgaver')}</span>
              <b>{T('{done} av {total}', tasks)}</b>
            </div>
          )}
          {seeColleagues && (
            <div className="ekv">
              <Icon name="users" />
              <span>{T('Kolleger')}</span>
              <b>{teamQ.isSuccess ? T('{n} på vakt nå', { n: onNow.length }) : '–'}</b>
            </div>
          )}
          <div className="ekv">
            <Icon name="clock" />
            <span>{T('Pause')}</span>
            <b>{breakForEmployee(tenant)}</b>
          </div>
          {main && seeColleagues && (
            <>
              <h3 className="eh3">{T('Jobber samtidig')}</h3>
              {alongside.length ? (
                alongside.slice(0, 4).map((c) => (
                  <div className="eperson" key={c.id}>
                    <Avatar u={c.user} />
                    <span className="ebody">
                      <b>{fullName(c.user)}</b>
                      <span>
                        {shiftName(c)}, <span className="mono">{span(c)}</span>
                      </span>
                    </span>
                  </div>
                ))
              ) : teamQ.isError ? (
                <LoadError what={T('kollegenes vakter')} onRetry={() => teamQ.refetch()} />
              ) : teamQ.isSuccess ? (
                <p className="hint">{T('Ingen andre på vakt samtidig.')}</p>
              ) : (
                <p className="hint">{T('Henter…')}</p>
              )}
              {alongside.length > 4 && (
                <Link className="elink" to="/min/laget">
                  {T('Se hele laget')} →
                </Link>
              )}
            </>
          )}
        </Card>
      </div>

      <div className="egrid3">
        {main && tasks.total > 0 && (
          <Card title={T('Oppgaver')} sub={T('{done} av {total} fullført', tasks)} testid="today-tasks">
            <Progress {...tasks} />
            <ul className="etasks">
              {(main.taskAssignments ?? []).slice(0, 5).map((t) => (
                <li key={t.id} className={t.status === 'completed' ? 'done' : ''}>
                  <Icon name={t.status === 'completed' ? 'check' : 'square'} />
                  <span>{t.shiftTask?.name ?? ''}</span>
                  {t.status !== 'completed' && (
                    <small>{t.status === 'in-progress' ? T('Pågår') : T('Åpen')}</small>
                  )}
                </li>
              ))}
            </ul>
            <button type="button" className="btn w" onClick={() => setDetail(main)}>
              <Icon name="list" />
              {T('Se alle oppgaver')}
            </button>
          </Card>
        )}
        <Card
          title={T('Neste vakter')}
          actions={
            <Link className="elink" to="/min/plan">
              {T('Se hele planen')} →
            </Link>
          }
          testid="next-shifts"
        >
          {shiftsQ.isError ? (
            // a failed fetch is not «Ingen vakter» (#272 M-02)
            <LoadError what={T('vaktene dine')} onRetry={() => shiftsQ.refetch()} />
          ) : shiftsQ.isPending ? (
            <p className="hint" style={{ margin: 0 }}>
              {T('Henter vaktene dine …')}
            </p>
          ) : later.length ? (
            later
              .slice(0, 3)
              .map((s) => <ShiftRow key={s.id} s={s} place={tenant?.name} onOpen={() => setDetail(s)} />)
          ) : (
            <p className="hint" style={{ margin: 0 }}>
              {T('Ingen vakter de neste to ukene.')}
            </p>
          )}
        </Card>
      </div>

      {(open.length > 0 || waiting.length > 0 || me) && (
        <div className="egrid">
          <div>
            {open.length > 0 && (
              <Card
                title={
                  <>
                    {T('Ledige vakter')}{' '}
                    <Chip tone="act" small>
                      {open.length === 1 ? T('1 ledig vakt') : T('{n} ledige vakter', { n: open.length })}
                    </Chip>
                  </>
                }
                actions={
                  <Link className="elink" to="/min/plan">
                    {T('Se alle ledige vakter')} →
                  </Link>
                }
                testid="open-shifts"
              >
                {open.slice(0, 2).map((s) => (
                  <OpenRow key={s.id} s={s} a={a} onOpen={() => setOpenDetail(s)} />
                ))}
              </Card>
            )}
            {waiting.length > 0 && (
              <Card title={T('Vaktbytte venter på deg')} testid="swaps-waiting">
                <SwapList swaps={waiting} />
              </Card>
            )}
          </div>
          {me && me.vacation_days_balance != null && (
            <Card title={T('Ferie')} testid="vacation">
              <p className="ebal">
                <b>{T('{n} dager igjen', { n: me.vacation_days_balance })}</b>
                {me.total_vacation_days_per_year != null && (
                  <span>
                    {' '}
                    {T('av {n} dager i {year}', {
                      n: me.total_vacation_days_per_year,
                      year: day0.slice(0, 4),
                    })}
                  </span>
                )}
              </p>
              {me.total_vacation_days_per_year ? (
                // days used, neutral: a full year left is an empty bar (#272 B-40)
                <Progress
                  done={me.total_vacation_days_per_year - me.vacation_days_balance}
                  total={me.total_vacation_days_per_year}
                  used
                />
              ) : null}
              <Link className="btn w" to="/min/fravaer">
                <Icon name="sun" />
                {T('Søk om fravær')}
              </Link>
            </Card>
          )}
        </div>
      )}

      <ShiftDrawer shift={shown} onClose={() => setDetail(null)} st={st} todays={todays} />
      <OpenDrawer s={openDetail} a={a} onClose={() => setOpenDetail(null)} />
    </>
  );
}
